import Button from 'components/Common/Button';
import Switch from 'components/Common/Switch';
import Grid from '@mui/material/Grid2';
import { useForm } from 'react-hook-form';
import { Box, Typography, Paper, useTheme, ClickAwayListener, Tooltip, IconButton } from '@mui/material';
import BusinessPricingIcon from 'assets/icons/BusinessPricingIcon';
import { useGetAgencyConfiguration, useUpdateAgencyConfiguration } from 'apis/agency/configuration/hook';
import Input from 'components/Common/Input';
import InfoIcon from 'assets/icons/InfoIcon';
import { useState } from 'react';
import CopyIcon from 'assets/icons/CopyIcon';
import { Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { pricingSchema } from 'utils/schema/validator/AgencyConfigureValidation';
import { BACKEND_URL } from 'env';

const Pricing = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { data: configuration } = useGetAgencyConfiguration();
  const { mutateAsync: updateConfiguration, isPending: updatingConfigs } = useUpdateAgencyConfiguration();
  const { control, handleSubmit, watch } = useForm({
    resolver: zodResolver(pricingSchema),
    values: {
      configuration: configuration
    }
  });
  const isBillingEnabled = watch('configuration.billing_enabled');
  const onSubmit = (data: any) => {
    updateConfiguration(data);
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleCopy = async () => {
    const url = `${BACKEND_URL}/webhooks/stripe`;
    await navigator.clipboard.writeText(url);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
    handleCopy();
  };

  return (
    <Box p={4}>
      <Typography variant='h5' fontWeight='bold' display='flex' alignItems='center' gap={1} mb={1}>
        <BusinessPricingIcon /> Stripe Billing Configuration
      </Typography>
      <Typography variant='body2' color='text.secondary' mb={2}>
        Configure how your businesses will be billed through your Stripe account. When enabled, all your businesses will
        use your Stripe account for billing and buying credits.
      </Typography>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #f3f3f3' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
          <Box>
            <Typography fontWeight='600'>Enable On-Platform Billing for this Business</Typography>
            <Typography color='text.secondary' fontSize={14}>
              Enable to use our platform for billing this business, or disable to handle billing offline
            </Typography>
          </Box>
          <Switch name='configuration.billing_enabled' control={control} />
        </Box>
        {!isBillingEnabled && (
          <Typography
            fontSize={14}
            color='black'
            bgcolor='rgba(240, 239, 239, 0.9)'
            border='1px solid rgba(240, 239, 239, 0.9)'
            padding={1.5}
            borderRadius={2}
          >
            You've chosen to handle billing offline. Your businesses will not be charged through the platform. You will
            need to establish your own billing system outside of this application.
          </Typography>
        )}
        {isBillingEnabled && (
          <>
            <Grid
              container
              spacing={2}
              paddingTop={1}
              bgcolor={theme.palette.background.paper}
              borderTop='1px solid rgb(229, 224, 224)'
            >
              <Grid size={12}>
                <Typography my={0.5} fontSize={14} fontWeight={500}>
                  Secret Key
                </Typography>
                <Input name='configuration.stripe_secret_key' control={control} size='small' fullWidth />
              </Grid>
              <Grid size={12}>
                <Typography my={0.5} fontSize={14} fontWeight={500}>
                  Publishable Key
                </Typography>
                <Input name='configuration.stripe_publishable_key' control={control} size='small' fullWidth />
              </Grid>
              <Grid size={12}>
                <Typography my={0.5} fontSize={14} fontWeight={500}>
                  Webhook Secret
                </Typography>
                <Input name='configuration.stripe_webhook_secret' control={control} size='small' fullWidth />
              </Grid>
              <Grid size={12}>
                <Typography my={0.5} fontSize={14} fontWeight={500}>
                  Cost Per Minute
                </Typography>
                <Input
                  name='configuration.per_min_cost'
                  type='number'
                  control={control}
                  InputProps={{
                    startAdornment: <Typography mx={1}>$</Typography>
                  }}
                  size='small'
                  fullWidth
                />
                <Typography variant='body2' my={0.5}>
                  Set the default cost per minute for calls made by your businesses
                </Typography>
              </Grid>
            </Grid>
            <Box
              sx={{
                backgroundColor: '#F0F6FF',
                border: '1px solid #D0E3FF',
                mt: 5,
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box display='flex' mb={2}>
                  <Box
                    component='span'
                    sx={{
                      width: 24,
                      height: 24,
                      mt: '2px',
                      color: '#1E66F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <InfoIcon />
                  </Box>
                  <Typography fontWeight={600} mt='2px' color='#1E66F5'>
                    Stripe Webhook Setup
                  </Typography>
                </Box>

                <Typography variant='body2' color='#1E66F5'>
                  To complete your Stripe integration, create a webhook in your Stripe Dashboard:
                </Typography>

                <Box
                  mt={2}
                  display='flex'
                  borderRadius={1}
                  justifyContent='space-between'
                  pl={1}
                  sx={{ backgroundColor: 'white', width: '100%' }}
                >
                  <Box display='flex' gap='3px'>
                    <Typography color='#1E66F5' fontWeight={600} fontSize={14} mt={1}>
                      Webhook URL:
                    </Typography>
                    <Typography fontSize={14} mt='8px'>
                      {BACKEND_URL}/webhooks/stripe
                    </Typography>
                  </Box>

                  <ClickAwayListener onClickAway={handleTooltipClose}>
                    <Tooltip
                      PopperProps={{ disablePortal: true }}
                      onClose={handleTooltipClose}
                      open={open}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      placement='top'
                      title='Copied'
                    >
                      <IconButton onClick={handleTooltipOpen}>
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                </Box>

                <Typography fontSize={14} color='#1E66F5'>
                  Make sure to select and activate the checkout.session.completed event.
                </Typography>

                <Link
                  to='#'
                  style={{
                    fontSize: '13px',
                    color: '#1E66F5',
                    textDecoration: 'underline'
                  }}
                >
                  View Detailed Webhook Setup Instructions
                </Link>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      <Box display='flex' justifyContent='flex-end' gap={2} mt={4}>
        <Button variant='contained' isPending={updatingConfigs} color='primary' onClick={handleSubmit(onSubmit)}>
          Save Pricing
        </Button>
      </Box>
    </Box>
  );
};

export default Pricing;
